import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
// -
import DateTimesUtils from "core/utils/DateTimesUtils";
import {CRITERIA_TITLES} from "./ModelsDynamic";

const toInteger = [
    "risk_threshold",
    "target_threshold",
    "tomorrow_threshold",
    "c4_threshold",
    "c5_threshold",
    "c6_threshold",
    "forecast_days",
];

// Máximo de días (rutas) a simular: más allá la previsión ya no es fiable
const MAX_FORECAST_DAYS = 7;

// Umbral de cada C: campo de priorities, texto corto y leyenda (title)
const THRESHOLDS = [
    {name: "risk_threshold", label: "C1 nivel ≥", title: CRITERIA_TITLES.c1},
    {name: "target_threshold", label: "C2 fin ruta ≥", title: CRITERIA_TITLES.c2},
    {name: "tomorrow_threshold", label: "C3 Prox. Ruta ≥", title: CRITERIA_TITLES.c3},
];

// Umbral de C4/C5/C6 (solo los activos en Identificación): campo de priorities y horas de params
const NO_DATA_THRESHOLDS = [
    {name: "c4_threshold", check: "c4", hours: "c4_hours", label: "C4"},
    {name: "c5_threshold", check: "c5", hours: "c5_hours", label: "C5"},
    {name: "c6_threshold", check: "c6", hours: "c6_hours", label: "C6"},
];

const Field = ({label, title, children}) => (
    <div className="input-group input-group-sm me-2 mb-1" style={{width: "auto"}} title={title}>
        <span className="input-group-text">{label}</span>
        {children}
    </div>
);

/*
 * Barra compacta de la vista previa: umbral de cada C, inicio de ruta, días de previsión y acciones,
 * y debajo una línea con los totales. Ocupa poco para dejar sitio a las tablas.
 * La ventana de ruta empieza en window_start y dura la duración máxima de la plantilla.
 */
const TabPreviewOverflowParams = ({
    formData,
    setFormData,
    totals,
    routeMinutes,
    onForecast,
    onDownload,
    onSubmit,
    programmings = [],
}) => {
    const navigate = useNavigate();
    const priorities = formData.priorities;

    const handleChange = (e) => {
        let {name, value} = e.target;
        if (toInteger.includes(name)) {
            value = value === "" ? "" : parseInt(value, 10);
        }
        // - el máximo del input solo limita las flechas: también se corta lo escrito a mano
        if (name === "forecast_days" && value !== "") {
            value = Math.min(value, MAX_FORECAST_DAYS);
        }
        setFormData({...formData, priorities: {...priorities, [name]: value}});
    };

    const handleProgramming = (e) => {
        const value = e.target.value;
        setFormData({...formData, programming: value === "" ? "" : parseInt(value, 10)});
    };

    // segunda línea: umbrales de los C4/C5/C6 activos en Identificación
    const noDataThresholds = NO_DATA_THRESHOLDS.filter((threshold) => formData.params[threshold.check]);

    // ahorro: teórico (sin Azure, 5 min entre puntos) menos el real de Azure
    const theoretical = totals?.theoretical_minutes ?? null;
    const saved = theoretical !== null && routeMinutes !== null ? theoretical - routeMinutes : null;
    const time = (minutes) => (minutes !== null && minutes !== undefined ? DateTimesUtils.minutesToTime(minutes) : "-");

    return (
        <div className="mb-2">
            <div className="d-flex flex-wrap align-items-center">
                {THRESHOLDS.map((threshold) => (
                    <Field key={threshold.name} label={threshold.label} title={threshold.title}>
                        <input
                            type="number"
                            className="form-control text-end"
                            style={{width: "65px"}}
                            name={threshold.name}
                            min="0"
                            max="100"
                            value={priorities[threshold.name] ?? priorities.risk_threshold}
                            onChange={handleChange}
                        />
                        <span className="input-group-text">%</span>
                    </Field>
                ))}
                <Field label="Inicio" title="Hora de inicio de la ruta">
                    <input
                        type="time"
                        className="form-control"
                        name="window_start"
                        value={priorities.window_start}
                        onChange={handleChange}
                    />
                </Field>
                <Field label="Sim. Días" title="Simulación de días">
                    <input
                        type="number"
                        className="form-control text-end"
                        style={{width: "55px"}}
                        name="forecast_days"
                        min="1"
                        max={MAX_FORECAST_DAYS}
                        value={priorities.forecast_days}
                        onChange={handleChange}
                    />
                </Field>
                <button type="button" className="btn btn-sm btn-outline-secondary me-2 mb-1" onClick={onForecast}>
                    <FontAwesomeIcon icon="eye" className="me-1" /> Calcular
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-2 mb-1"
                    onClick={onDownload}
                    disabled={!totals}>
                    <FontAwesomeIcon icon="fa-regular fa-file-excel" className="me-1" /> Excel
                </button>
                <div className="ms-auto mb-1">
                    <button type="button" className="btn btn-sm btn-primary me-2" onClick={onSubmit}>
                        Guardar
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigate("/route-management/routes-dynamic/")}>
                        Cancelar
                    </button>
                </div>
            </div>
            <div className="d-flex flex-wrap align-items-center mt-2">
                {noDataThresholds.map((threshold) => (
                    <Field
                        key={threshold.name}
                        label={`${threshold.label} >${formData.params[threshold.hours]} h ≥`}
                        title={CRITERIA_TITLES[threshold.check]}>
                        <input
                            type="number"
                            className="form-control text-end"
                            style={{width: "65px"}}
                            name={threshold.name}
                            min="0"
                            max="100"
                            value={priorities[threshold.name]}
                            onChange={handleChange}
                        />
                        <span className="input-group-text">%</span>
                    </Field>
                ))}
                {/* separada de los C4-6 para que destaque */}
                <div className={noDataThresholds.length ? "ms-3 ps-3 border-start" : ""}>
                    <Field label="Programación" title="Programación de la plantilla">
                        <select
                            className="form-select"
                            style={{maxWidth: "240px"}}
                            value={formData.programming ?? ""}
                            onChange={handleProgramming}>
                            <option value="">Ninguna</option>
                            {programmings.map((programming) => (
                                <option key={programming.id} value={programming.id}>
                                    {programming.name}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </div>
            <div className="small text-muted">
                Duración máx. <strong>{time(totals?.window_minutes)}</strong> · Teórico{" "}
                <strong>{time(theoretical)}</strong> · Calculado <strong>{time(routeMinutes)}</strong> · Ahorro{" "}
                <strong>{saved !== null ? time(Math.max(saved, 0)) : "-"}</strong> · A recoger{" "}
                <strong>{totals ? `${totals.collect}/${totals.evaluated}` : "-"}</strong>
            </div>
            <hr className="my-2" />
        </div>
    );
};

export default TabPreviewOverflowParams;
